import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiEye } from "react-icons/fi";
import { FiEyeOff } from "react-icons/fi";
import useForm from "../../hooks/useForm";
import { useThunk } from "../../hooks/useThunk";
import { updatePassword, validateToken } from "../../store";
import { errorMessages } from "../../constants/frontendErrorMessages";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import CustomInput from "../../components/Form/Input";
import CustomButton from "../../components/Form/CustomButton";
import showToast from "../../utils/toastNotifications";

const SetNewPassword = () => {
  const navigate = useNavigate();
  const [linkVerified, setLinkVerified] = useState(true);
  const [verifyError, setVerifyError] = useState(null);
  const [doVerifyPasswordForgot, isVerifying] = useThunk(validateToken);
  const [doChangePassword, isPasswordChanging] = useThunk(updatePassword);
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setConfirmShowPassword] = useState(false);

  const token = searchParams.get("token");
  const id = searchParams.get("id");

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    clearError,
  } = useForm(
    {
      password: "",
      confirmPassword: "",
    },
    {
      password: true,
      confirmPassword: true,
    },
    {
      password: COMMON_FIELD_TYPES.PASSWORD_FIELD,
      confirmPassword: COMMON_FIELD_TYPES.CONFIRM_PASSWORD,
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      password: true,
      confirmPassword: true,
    });

    if (validateForm()) {
      const result = await doChangePassword({ token, id, data: formData });

      if (result?.success) {
        navigate("/signin", { replace: true });
        showToast("success", result.response?.message);
      } else {
        showToast("error", result.error?.message);
      }
    }
  };

  useEffect(() => {
    if (!token || !id) {
      setLinkVerified(false);
      setVerifyError(errorMessages.FAILED_RESET_PASSWORD);
      return;
    }
    doVerifyPasswordForgot({ id, token }).then((result) => {
      if (result?.success) {
        setLinkVerified(true);
      } else {
        setLinkVerified(false);
        setVerifyError(result?.error?.message);
      }
    });
  }, [id, token, doVerifyPasswordForgot]);

  const handleOkayButton = () => {
    navigate("/forgot-password", { replace: true });
  };

  return (
    <div
      className="relative w-full max-w-[516px] h-auto lg:h-[535px] mx-auto rounded-[20px] bg-white pt-[30px] px-[30px] pb-8"
      style={{
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
      }}
    >
      <h1 className="text-level-4 sm:text-[30px] text-[#4772F1] font-semibold font-alexandria leading-normal">
        Reset Password
      </h1>
      <p className="mt-6 text-base font-normal leading-5 tracking-tight sm:mt-8 text-dark-blue font-alexandria">
        Enter a new password below to change your password.
      </p>

      <form onSubmit={handleSubmit} className="mt-5 sm:mt-8 flex flex-col gap-[28px]">
        <div className="relative">
          <CustomInput
            type= {showPassword ? "text" : "password"}
            label="New Password"
            name="password"
            isRequired={true}
            value={formData.password}
            placeholder="Enter new password"
            error={touched.password && errors.password}
            onBlur={() => handleBlur("password", formData.password)}
            // onFocus={() => clearError("password")}
            onChange={(e) => handleInputChange("password", e.target.value)}
            className="w-full h-14 mt-0 px-3 rounded-lg border border-[#B9C1CA] text-[#9D9D9D]"
            labelStyle="font-alexandria text-base font-medium leading-5 text-[#0F161E]"
            inputStyle="bg-[#4772F1] bg-opacity-[0.03]"
          />
          <span
              className="absolute right-1 sm:right-3 top-[53%] sm:top-[60%] transform -translate-y-1/2 cursor-pointer p-2"
              onClick={() => setShowPassword(!showPassword)}
          >
              {showPassword ? ( 
                  <FiEye className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" /> 
              ) : ( 
                  <FiEyeOff className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" /> 
              )}
          </span>
        </div>

        <div className="relative -mt-3 sm:mt-1">
          <CustomInput
            type={showConfirmPassword ? "text" : "password"}
            name="confirmPassword"
            label="Confirm Password"
            isRequired={true}
            value={formData.confirmPassword}
            placeholder="Enter confirm password"
            error={touched.confirmPassword && errors.confirmPassword}
            onBlur={() =>
              handleBlur("confirmPassword", formData.confirmPassword)
            }
            // onFocus={() => clearError("confirmPassword")}
            onChange={(e) =>
              handleInputChange("confirmPassword", e.target.value)
            }
            className="w-full h-14 mt-0 px-3 rounded-lg border border-[#B9C1CA] text-[#9D9D9D]"
            labelStyle="font-alexandria text-base font-medium leading-5 text-[#0F161E]"
            inputStyle="bg-[#4772F1] bg-opacity-[0.03]"
          />
          <span
              className="absolute right-1 sm:right-3 top-[53%] sm:top-[60%] transform -translate-y-1/2 cursor-pointer p-2"
              onClick={() => setConfirmShowPassword(!showConfirmPassword)}
          >
              {showConfirmPassword ? ( 
                  <FiEye className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" /> 
              ) : ( 
                  <FiEyeOff className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" /> 
              )}
          </span>
        </div>

        <div className="flex justify-center -mt-2 sm:mt-2">
          <CustomButton
            type="submit"
            className="text-[#F7FAFC] text-[20px] font-bold rounded-lg w-[456px] h-[60px] bg-gradient-to-b from-[#4772F1] to-[#873FE2]"
            buttonText="Reset Password"
            loading={isPasswordChanging}
          />
        </div>

        <div className="text-center text-[#00000080] font-[Alexandria] -mt-[10px]">
          Have an account?{" "}
          <span
            className="text-[#4772F1] underline cursor-pointer"
            onClick={() => navigate("/signin")}
          >
            Login
          </span>
        </div>
      </form>
    </div>
  );
};

export default SetNewPassword;
